/**
 * LANES registry — the five-lane canon (CP-R878).
 * Loads schema/lanes.json (single canonical source per ADR-0017), Zod-validates
 * at module load, FAIL CLOSED. EXACTLY five lanes: mtss, sped, s504, el, health.
 * Behavior is a DOMAIN inside MTSS, never a lane — a sixth lane, or a 'behavior'
 * lane, refuses to load.
 */
import { z } from 'zod'
import { AppError } from '../errors/app-error'
import lanesJson from '../../../../schema/lanes.json'

export const CANONICAL_LANE_IDS = ['mtss', 'sped', 's504', 'el', 'health'] as const
export const LaneRegistryIdSchema = z.enum(CANONICAL_LANE_IDS)
export type LaneRegistryId = z.infer<typeof LaneRegistryIdSchema>

export const LaneRegistryEntrySchema = z
  .object({
    id: LaneRegistryIdSchema,
    label: z.string().min(1),
    accent: z.string().min(1),
    accentBg: z.string().min(1),
    bodyClass: z.string().min(1),
    manual: z.boolean(),
    keepsMonitor: z.boolean(),
    activePredicate: z.string().optional(),
    eligiblePredicate: z.string().optional(),
    activeFunction: z.string().optional(),
    eligibleFunction: z.string().optional(),
    gatedForms: z.union([z.string(), z.array(z.string())]).optional(),
    planForms: z.array(z.string()).optional(),
    addendum: z.string().optional(),
    aliases: z.array(z.string()),
  })
  .strict()

export type LaneRegistryEntry = z.infer<typeof LaneRegistryEntrySchema>

const LanesFileSchema = z
  .object({
    description: z.string(),
    version: z.string(),
    target: z.string(),
    order: z.array(z.string()).length(5),
    lanes: z.record(LaneRegistryEntrySchema),
    bridgePaths: z.record(z.object({ note: z.string() }).strict()),
  })
  .strict()

function loadLanes(): { order: readonly LaneRegistryId[]; lanes: ReadonlyMap<LaneRegistryId, LaneRegistryEntry> } {
  const parsed = LanesFileSchema.safeParse(lanesJson)
  if (!parsed.success) {
    throw AppError.validation(
      'LANES registry failed validation — refusing to load a malformed lane canon (fail closed)',
      parsed.error.issues,
    )
  }
  const keys = Object.keys(parsed.data.lanes)
  if (keys.length !== 5) {
    throw AppError.validation(
      `LANES registry: the canon is EXACTLY five lanes (mtss, sped, s504, el, health) — found ${keys.length}`,
    )
  }
  if (keys.includes('behavior') || parsed.data.order.includes('behavior')) {
    throw AppError.validation(
      'LANES registry: behavior is a DOMAIN inside MTSS, never a lane (legacy canon 2026-07-09)',
    )
  }
  const canon = new Set<string>(CANONICAL_LANE_IDS)
  for (const key of keys) {
    if (!canon.has(key)) {
      throw AppError.validation(`LANES registry: unknown lane key '${key}' — the five-lane canon admits no additions`)
    }
    const entry = parsed.data.lanes[key]
    if (!entry) {
      throw AppError.validation(`LANES registry: lane '${key}' has no entry`)
    }
    if (entry.id !== key) {
      throw AppError.validation(
        `LANES registry: lane keyed '${key}' carries id '${entry.id}' — key and id must agree (the '504' vs 's504' drift class)`,
      )
    }
  }
  for (const o of parsed.data.order) {
    if (!canon.has(o) || !keys.includes(o)) {
      throw AppError.validation(`LANES registry: order names '${o}' which is not a defined canonical lane`)
    }
  }
  const map = new Map<LaneRegistryId, LaneRegistryEntry>()
  for (const id of CANONICAL_LANE_IDS) {
    const entry = parsed.data.lanes[id]
    if (!entry) {
      throw AppError.validation(`LANES registry: canonical lane '${id}' is missing — all five lanes are required`)
    }
    map.set(id, entry)
  }
  return { order: parsed.data.order as LaneRegistryId[], lanes: map }
}

const loaded = loadLanes()

/** The five lanes in display order. Frozen. */
export const LANE_ORDER: readonly LaneRegistryId[] = Object.freeze([...loaded.order])

/** Lane entries by id — exactly five, validated. */
export const LANE_REGISTRY: ReadonlyMap<LaneRegistryId, LaneRegistryEntry> = loaded.lanes

export function isLaneRegistryId(id: string): id is LaneRegistryId {
  return (CANONICAL_LANE_IDS as readonly string[]).includes(id)
}

/** Resolve a lane by id. THROWS on unknown id — there is no sixth lane. */
export function getLane(id: string): LaneRegistryEntry {
  if (!isLaneRegistryId(id)) {
    throw AppError.notFound(`Unknown lane id '${id}' — the canon is exactly five lanes (behavior is a domain, not a lane)`)
  }
  const entry = LANE_REGISTRY.get(id)
  if (!entry) {
    throw AppError.internal(`LANES registry inconsistent: canonical id '${id}' missing after validation`)
  }
  return entry
}
